<template>
    <div class="uploadForm">
        <StepByStep 
            :steps="steps"
            :validation-rules="validationRules"
            @step-change="onStepChange"
            @finish="handleUpload"
        >
            <!-- Step 1: Tipo de Pessoa -->
            <template #step-0="{ nextStep }">
                <div class="step-content">                    
                    <div class="step-body">
                        <person-type-selection 
                            v-model="formData.personType" 
                            :auto-advance="true"
                            @update:modelValue="handlePersonTypeChange($event, nextStep)"
                        />
                    </div>
                </div>
            </template>

            <!-- Step 2: Detalhes do Arquivo -->
            <template #step-1="{ nextStep, prevStep, canNext }">
                <div class="step-content">
                    <div class="step-body">
                        <!-- Seção para Pessoa Jurídica -->
                        <div v-if="formData.personType === PERSON_TYPES.JURIDICA" class="modal-section">
                            <h4>Informações da Empresa</h4>

                            <div class="row mb_30">
                                <!-- Checkbox para documento de empresa -->
                                <div class="modal-field grid_col_6">
                                    <sw-checkbox
                                        v-model="formData.isCompanyDocument"
                                        label="Documento é de uma empresa"
                                        @update:modelValue="formData.client = ''"
                                    />
                                </div>

                                <!-- Empresa (se marcado o checkbox) -->
                                <div v-if="formData.isCompanyDocument" class="modal-field grid_col_6 grid_col_s_12 grid_col_xs_12">
                                    <div class="uploadForm_fieldWithButton">
                                        <sw-select
                                            v-model="formData.client"
                                            label="Empresa *"
                                            placeholder="Selecione a empresa"
                                            :options="clientOptions"
                                            search
                                            search-label="Buscar empresa"
                                            search-icon="search"
                                            required
                                        />
                                        <sw-button
                                            circle-icon
                                            icon="plus"
                                            color="blue"
                                            title="Criar nova empresa"
                                            @click="createNewClient"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Seção de Tipo e Categoria -->
                        <div class="modal-section">
                            <h4>Classificação do Arquivo</h4>

                            <div class="row mb_30">
                                <!-- Tipo de arquivo -->
                                <div class="modal-field grid_col_6 grid_col_s_12 grid_col_xs_12">
                                    <sw-select
                                        v-model="formData.type"
                                        label="Tipo de arquivo *"
                                        placeholder="Selecione o tipo"
                                        :options="typeOptions"
                                        search
                                        search-label="Buscar tipo de arquivo"
                                        search-icon="search"
                                        required
                                    />
                                </div>

                                <!-- Categoria do arquivo -->
                                <div class="modal-field grid_col_6 grid_col_s_12 grid_col_xs_12">
                                    <div class="uploadForm_fieldWithButton">
                                        <sw-select
                                            v-model="formData.category"
                                            label="Categoria do arquivo"
                                            placeholder="Selecione uma categoria (opcional)"
                                            :options="categoryOptions"
                                            search
                                            search-label="Buscar categoria"
                                            search-icon="search"
                                        />
                                        <sw-button
                                            circle-icon
                                            icon="plus"
                                            color="blue"
                                            title="Criar nova categoria"
                                            @click="createNewCategory"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Seção de Upload e Informações -->
                        <div class="modal-section">
                            <h4>Arquivo e Identificação</h4>

                            <div class="row mb_20 mt_20">
                                <!-- Upload do arquivo -->
                                <div class="modal-field grid_col_12">
                                    <sw-input-file
                                        v-model="selectedFile"
                                        label="Upload do arquivo *"
                                        label-button="Selecionar arquivo"
                                        accept=".pdf,.jpeg,.png,.doc,.docx"
                                        @update:modelValue="handleFileSelect"
                                    />
                                </div>
                            </div>

                            <div class="row mb_20">
                                <!-- Nome do arquivo -->
                                <div class="modal-field grid_col_12">
                                    <sw-input
                                        v-model="formData.name"
                                        label="Nome do arquivo *"
                                        placeholder="Ex: CNH - João Silva"
                                        required
                                    />
                                </div>
                            </div>

                        </div>

                    </div>
                    
                    <div class="step-actions">
                        <sw-button
                            name="Voltar"
                            color="gray"
                            @click="prevStep"
                        />
                        <div class="step-actions_spacer"></div>
                        <sw-button
                            name="Continuar"
                            color="blue"
                            :disabled="!canNext"
                            @click="nextStep"
                        />
                    </div>
                </div>
            </template>

            <!-- Step 3: Vencimento (Opcional) -->
            <template #step-2="{ nextStep, prevStep, canNext }">
                <div class="step-content">
                    <div class="step-body">
                        <div class="modal-section">
                            <h4>Configuração de Vencimento</h4>

                            <div class="row mb_20 mt_20">
                                <!-- Checkbox para habilitar vencimento -->
                                <div class="modal-field grid_col_12">
                                    <sw-checkbox
                                        v-model="formData.hasExpiryDate"
                                        label="Este documento possui data de vencimento"
                                        @update:modelValue="handleExpiryToggle"
                                    />
                                </div>
                            </div>

                            <div v-if="formData.hasExpiryDate" class="row mb_20">
                                <!-- Data de vencimento -->
                                <div class="modal-field grid_col_12">
                                    <sw-input
                                        v-model="formData.expiryDate"
                                        label="Data de vencimento *"
                                        :dateView="true"
                                        :minDate="moment().toDate()"
                                        placeholder="Selecione a data"
                                        required
                                    />
                                </div>
                            </div>

                            <AlertMessage
                                :variant="ALERT_VARIANTS.INFO"
                                title="Quando configurar vencimento?"
                                description="Configure vencimento para documentos que expiram:<br><strong>Pessoa Física:</strong> CNH, passaporte, RG, título de eleitor<br><strong>Pessoa Jurídica:</strong> licenças, alvarás, certidões, contratos, seguros<br><br>Deixe desmarcado para documentos permanentes (certidão de nascimento, escrituras, etc.)"
                            />
                        </div>
                    </div>

                    <div class="step-actions">
                        <sw-button
                            name="Voltar"
                            color="gray"
                            @click="prevStep"
                        />
                        <div class="step-actions_spacer"></div>
                        <sw-button
                            name="Continuar"
                            color="blue"
                            :disabled="!canNext"
                            @click="nextStep"
                        />
                    </div>
                </div>
            </template>

            <!-- Step 4: Revisar e Confirmar -->
            <template #step-3="{ prevStep }">
                <div class="step-content">
                    <div class="step-body">
                        <div class="review-summary">
                            <div class="review-section modal-section">
                                <h4 class="review-section_title">Tipo de Pessoa</h4>
                                <div class="review-item">
                                    <span class="review-item_label">Selecionado:</span>
                                    <span class="review-item_value">{{ formData.personType === PERSON_TYPES.FISICA ? 'Pessoa Física' : 'Pessoa Jurídica' }}</span>
                                </div>
                            </div>

                            <div class="review-section modal-section">
                                <h4 class="review-section_title">Detalhes do Arquivo</h4>
                                <div v-if="formData.isCompanyDocument && formData.client" class="review-item">
                                    <span class="review-item_label">Empresa:</span>
                                    <span class="review-item_value">{{ getClientLabel(formData.client) }}</span>
                                </div>
                                <div class="review-item">
                                    <span class="review-item_label">Tipo:</span>
                                    <span class="review-item_value">{{ getTypeLabel(formData.type) }}</span>
                                </div>
                                <div class="review-item">
                                    <span class="review-item_label">Nome:</span>
                                    <span class="review-item_value">{{ formData.name }}</span>
                                </div>
                                <div class="review-item">
                                    <span class="review-item_label">Arquivo:</span>
                                    <span class="review-item_value">{{ selectedFile?.name || 'Nenhum arquivo selecionado' }}</span>
                                </div>
                                <div v-if="formData.category" class="review-item">
                                    <span class="review-item_label">Categoria:</span>
                                    <span class="review-item_value">{{ getCategoryLabel(formData.category) }}</span>
                                </div>
                            </div>

                            <div class="review-section modal-section">
                                <h4 class="review-section_title">Vencimento</h4>
                                <div v-if="formData.hasExpiryDate" class="review-item">
                                    <span class="review-item_label">Data de vencimento:</span>
                                    <span class="review-item_value">{{ formatDate(formData.expiryDate) }}</span>
                                </div>
                                <div v-if="!formData.hasExpiryDate" class="review-item">
                                    <span class="review-item_label">Configuração:</span>
                                    <span class="review-item_value">Documento sem data de vencimento</span>
                                </div>
                            </div>

                        </div>
                    </div>

                    <div class="step-actions">
                        <sw-button
                            name="Voltar"
                            color="gray"
                            @click="prevStep"
                        />
                        <div class="step-actions_spacer"></div>
                        <sw-button
                            name="Salvar Arquivo"
                            color="green"
                            :disabled="!isFormValid"
                            @click="handleUpload"
                        />
                    </div>
                </div>
            </template>
        </StepByStep>

        <!-- Modal Criar Nova Categoria -->
        <sw-modal
            :visible="showCreateCategoryModal"
            :position-right="true"
            @update="closeCreateCategoryModal"
        >
            <template #header>
                <div class="createCategoryModal_header">
                    <div class="createCategoryModal_icon">
                        <sw-icon name="folder-plus" />
                    </div>
                    <div class="createCategoryModal_title">
                        <h3>Criar Nova Categoria</h3>
                        <span>Organize seus arquivos criando uma categoria personalizada</span>
                    </div>
                </div>
            </template>

            <template #content>
                <div class="createCategoryModal_content">
                    <div class="createCategoryForm">
                        <sw-input
                            v-model="newCategoryName"
                            label="Nome da categoria *"
                            placeholder="Ex: Contratos 2024"
                            :validation="categoryNameValidation"
                        />

                        <AlertMessage
                            :variant="ALERT_VARIANTS.INFO"
                            title="Dica"
                            description="Use nomes descritivos como 'Documentos Pessoais', 'Empresa XYZ' ou 'Família Silva'"
                        />

                        <div class="createCategoryForm_actions">
                            <sw-button
                                name="Cancelar"
                                color="gray"
                                @click="closeCreateCategoryModal"
                            />
                            <sw-button
                                name="Criar Categoria"
                                color="blue"
                                :disabled="!isCategoryNameValid"
                                @click="handleCreateCategory"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </sw-modal>

        <!-- Modal Criar Novo Cliente -->
        <sw-modal
            :visible="showCreateClientModal"
            :position-right="true"
            @update="closeCreateClientModal"
        >
            <template #header>
                <div class="createClientModal_header">
                    <div class="createClientModal_icon">
                        <sw-icon name="office" />
                    </div>
                    <div class="createClientModal_title">
                        <h3>Nova Empresa</h3>
                        <span>Adicione uma empresa para organizar seus documentos</span>
                    </div>
                </div>
            </template>

            <template #content>
                <div class="createClientModal_content">
                    <div class="createClientForm">
                        <sw-input
                            v-model="newClientName"
                            label="Nome da empresa *"
                            placeholder="Ex: Tech Solutions Ltda."
                            :validation="clientNameValidation"
                        />

                        <sw-input-file
                            v-model="newClientLogo"
                            label="Logo da empresa (opcional)"
                            placeholder="Clique ou arraste uma imagem"
                            accept="image/*"
                            :max-size="2"
                            help-text="Formatos aceitos: JPG, PNG, SVG (máx. 2MB)"
                        />

                        <AlertMessage
                            :variant="ALERT_VARIANTS.INFO"
                            title="Dica"
                            description="Use nomes completos como 'Tech Solutions Ltda.', 'Indústria Costa S.A.' ou 'Comercial Silva & Associados'"
                        />

                        <div class="createClientForm_actions">
                            <sw-button
                                name="Cancelar"
                                color="gray"
                                @click="closeCreateClientModal"
                            />
                            <sw-button
                                name="Cadastrar Empresa"
                                color="blue"
                                :disabled="!isClientNameValid"
                                @click="handleCreateClient"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </sw-modal>
    </div>
</template>

<script setup>
    import { ref, computed } from 'vue';
    import { loadingFull, notify } from 'sysweb-vue-components/src/global';
    import PersonTypeSelection from '@/components/forms/PersonTypeSelection.vue';
    import StepByStep from '@/components/steps/StepByStep.vue';
    import AlertMessage from '@/components/alerts/AlertMessage.vue';
    import { mockFileCategories, mockFileGroups, mockClients } from '@/data/mockData';
    import { PERSON_TYPES, ALERT_VARIANTS } from '@/constants';
    import moment from 'moment';

    // Configurar moment para português
    moment.locale('pt-br');

    const emit = defineEmits(['cancel', 'upload-success']);

    const selectedFile = ref(null);

    // Estados do modal de criar categoria
    const showCreateCategoryModal = ref(false);
    const newCategoryName = ref('');

    // Estados do modal de criar cliente
    const showCreateClientModal = ref(false);
    const newClientName = ref('');
    const newClientLogo = ref(null);

    // Steps configuration for StepByStep component
    const steps = ref([
        {
            title: 'Tipo de Pessoa',
            subtitle: 'Física ou Jurídica',
            description: 'Escolha se é pessoa física ou jurídica para ver as categorias adequadas'
        },
        {
            title: 'Detalhes do Arquivo',
            subtitle: 'Informações básicas',
            description: 'Preencha as informações básicas do seu arquivo'
        },
        {
            title: 'Vencimento',
            subtitle: 'Data de validade',
            description: 'Configure data de vencimento e alertas automáticos (opcional)'
        },
        {
            title: 'Revisar e Confirmar',
            subtitle: 'Resumo final',
            description: 'Revise todas as informações antes de salvar o arquivo'
        }
    ]);

    // Validation rules for each step
    const validationRules = ref({
        0: () => formData.value.personType !== '', // Step 1: Tipo de pessoa
        1: () => {
            // Step 2: Detalhes - validação básica
            const basicValid = formData.value.type !== '' && formData.value.name !== '' && selectedFile.value !== null;

            // Se pessoa jurídica, validar campos adicionais
            if (formData.value.personType === PERSON_TYPES.JURIDICA) {
                const clientValid = !formData.value.isCompanyDocument || formData.value.client !== '';
                return basicValid && clientValid;
            }

            return basicValid;
        },
        2: () => {
            // Step 3: Vencimento - se habilitou vencimento, deve ter data
            if (formData.value.hasExpiryDate) {
                return formData.value.expiryDate !== '';
            }
            return true; // Se não habilitou, sempre válido
        },
        3: () => true  // Step 4: Revisão sempre válido
    });

    // Handle step changes
    const onStepChange = (stepIndex) => {
        // Hook para mudanças de step (pode ser usado futuramente)
    };

    // Handle person type selection and auto-advance
    const handlePersonTypeChange = (personType, nextStep) => {
        if (personType) {
            nextStep();
        }
    };

    // Form data
    const formData = ref({
        personType: '',
        isCompanyDocument: false,
        client: '',
        type: '',
        name: '',
        category: '',
        hasExpiryDate: false,
        expiryDate: ''
    });

    // Options vindos do mock centralizado com selected dinâmico
    const typeOptions = computed(() => {
        let options = [];
        if (formData.value.personType === PERSON_TYPES.FISICA) {
            options = mockFileCategories.NATURAL;
        } else if (formData.value.personType === PERSON_TYPES.JURIDICA) {
            options = mockFileCategories.COMPANY;
        }

        // Marcar como selected baseado no valor atual
        return options.map(option => {
            const hasSuboptions = option.suboptions && option.suboptions.length > 0;

            return {
                ...option,
                // Se tem suboptions: NÃO pode selecionar a option principal (sempre false)
                // Se NÃO tem suboptions: pode selecionar normalmente
                selected: hasSuboptions ? false : option.value === formData.value.type,
                suboptions: option.suboptions?.map(sub => ({
                    ...sub,
                    selected: sub.value === formData.value.type
                }))
            };
        });
    });

    // Arrays base para permitir adicionar novos itens
    const categoryOptionsBase = ref([...mockFileGroups]);
    const clientOptionsBase = ref(
        mockClients.map(client => ({
            value: client.id,
            label: client.name
        }))
    );

    // Categorias usando mock centralizado com selected dinâmico
    const categoryOptions = computed(() => {
        return categoryOptionsBase.value.map(option => ({
            ...option,
            selected: option.value === formData.value.category
        }));
    });

    // (Removido ownershipOptions - agora usa checkbox)

    // Clientes usando mock centralizado com selected dinâmico
    const clientOptions = computed(() => {
        return clientOptionsBase.value.map(option => ({
            ...option,
            selected: option.value === formData.value.client
        }));
    });


    // Validação do formulário
    const isFormValid = computed(() => {
        const basicValid = formData.value.personType &&
                          formData.value.type &&
                          formData.value.name &&
                          selectedFile.value;

        // Se pessoa jurídica, validar campos adicionais
        if (formData.value.personType === PERSON_TYPES.JURIDICA) {
            const clientValid = !formData.value.isCompanyDocument || formData.value.client !== '';
            if (!basicValid || !clientValid) return false;
        } else if (!basicValid) {
            return false;
        }

        // Se habilitou vencimento, deve ter data
        if (formData.value.hasExpiryDate && !formData.value.expiryDate) {
            return false;
        }

        return true;
    });

    // Função para lidar com o toggle do vencimento
    const handleExpiryToggle = (enabled) => {
        if (!enabled) {
            formData.value.expiryDate = '';
        }
    };

    // Função para formatar datas
    const formatDate = (dateString) => {
        if (!dateString) return '';
        return moment(dateString).format('DD/MM/YYYY');
    };



    // Helper functions for review step
    const getCategoryLabel = (categoryValue) => {
        const category = categoryOptions.value.find(c => c.value === categoryValue);
        return category?.label || categoryValue;
    };

    const getTypeLabel = (typeValue) => {
        const allTypes = [...mockFileCategories.NATURAL, ...mockFileCategories.COMPANY];
        const type = allTypes.find(t => t.value === typeValue);
        return type?.label || typeValue;
    };

    // (Removida função getOwnershipLabel - não mais necessária)

    const getClientLabel = (clientValue) => {
        const client = clientOptions.value.find(c => c.value === clientValue);
        return client?.label || clientValue;
    };

    // Validação do nome da categoria
    const categoryNameValidation = computed(() => ({
        isValid: newCategoryName.value.trim().length >= 2,
        error: {
            message: newCategoryName.value.trim().length < 2 && newCategoryName.value.length > 0
                ? 'Nome deve ter pelo menos 2 caracteres'
                : ''
        }
    }));

    const isCategoryNameValid = computed(() =>
        newCategoryName.value.trim().length >= 2
    );

    // Validação do nome do cliente
    const clientNameValidation = computed(() => ({
        isValid: newClientName.value.trim().length >= 2,
        error: {
            message: newClientName.value.trim().length < 2 && newClientName.value.length > 0
                ? 'Nome deve ter pelo menos 2 caracteres'
                : ''
        }
    }));

    const isClientNameValid = computed(() =>
        newClientName.value.trim().length >= 2
    );

    // Função para abrir modal de criar nova categoria
    const createNewCategory = () => {
        showCreateCategoryModal.value = true;
        newCategoryName.value = '';
    };

    // Função para abrir modal de criar novo cliente
    const createNewClient = () => {
        showCreateClientModal.value = true;
        newClientName.value = '';
        newClientLogo.value = null;
    };

    // Função para simular recarregamento das categorias da API
    const reloadCategoriesFromApi = async () => {
        try {
            // Simular loading
            notify('Carregando categorias - Atualizando lista de categorias disponíveis.', 'info');

            // Simular delay da API
            await new Promise(resolve => setTimeout(resolve, 800));

            // Recarregar categorias do mock (simulando API)
            categoryOptions.value = [...mockFileGroups];

            console.log('Categorias recarregadas da API:', categoryOptions.value);

        } catch (error) {
            notify('Erro ao carregar categorias - Não foi possível carregar as categorias. Tente novamente.', 'error');
        }
    };

    // Função para fechar modal de categoria
    const closeCreateCategoryModal = () => {
        showCreateCategoryModal.value = false;
        newCategoryName.value = '';
    };

    // Função para fechar modal de cliente
    const closeCreateClientModal = () => {
        showCreateClientModal.value = false;
        newClientName.value = '';
        newClientLogo.value = null;
    };

    // Função para simular recarregamento dos clientes da API
    const reloadClientsFromApi = async () => {
        try {
            // Simular loading
            notify('Carregando clientes - Atualizando lista de clientes disponíveis.', 'info');

            // Simular delay da API
            await new Promise(resolve => setTimeout(resolve, 800));

            // Recarregar clientes do mock (simulando API)
            clientOptionsBase.value = mockClients.map(client => ({
                value: client.id,
                label: client.name
            }));

            console.log('Clientes recarregados da API:', clientOptions.value);

        } catch (error) {
            notify('Erro ao carregar clientes - Não foi possível carregar os clientes. Tente novamente.', 'error');
        }
    };

    // Função para criar a categoria
    const handleCreateCategory = async () => {
        if (!isCategoryNameValid.value) {
            notify('Nome inválido - Digite um nome com pelo menos 2 caracteres.', 'warning');
            return;
        }

        const categoryName = newCategoryName.value.trim();

        // Verificar se já existe
        const categoryExists = categoryOptions.value.some(category =>
            category.label.toLowerCase() === categoryName.toLowerCase()
        );

        if (categoryExists) {
            notify('Categoria já existe - Já existe uma categoria com este nome.', 'warning');
            return;
        }

        // Simular criação na API e recarregamento
        await createCategoryInApi(categoryName);
    };

    // Função para criar o cliente
    const handleCreateClient = async () => {
        if (!isClientNameValid.value) {
            notify('Nome inválido - Digite um nome com pelo menos 2 caracteres.', 'warning');
            return;
        }

        const clientName = newClientName.value.trim();

        // Verificar se já existe
        const clientExists = clientOptionsBase.value.some(client =>
            client.label.toLowerCase() === clientName.toLowerCase()
        );

        if (clientExists) {
            notify('Cliente já existe - Já existe um cliente com este nome.', 'warning');
            return;
        }

        // Simular criação na API e recarregamento
        await createClientInApi(clientName);
    };

    // Função para simular criação de categoria na API
    const createCategoryInApi = async (categoryName) => {
        try {
            // Simular loading de criação
            notify('Criando categoria - Salvando nova categoria na base de dados.', 'info');

            // Simular delay da API para criar
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Criar valor único baseado no nome
            const newValue = categoryName.toLowerCase()
                .replace(/[^a-z0-9\s]/g, '') // Remove caracteres especiais
                .replace(/\s+/g, '-'); // Substitui espaços por hífen

            // Simular recarregamento completo das categorias após criação
            await reloadCategoriesFromApi();

            // Adicionar a nova categoria criada (simula que voltou da API)
            categoryOptionsBase.value.push({
                value: newValue,
                label: categoryName
            });

            // Selecionar a nova categoria automaticamente
            formData.value.category = newValue;

            // Fechar modal e mostrar sucesso
            closeCreateCategoryModal();

            notify(`Categoria criada - Categoria "${categoryName}" criada e selecionada com sucesso.`, 'success');

        } catch (error) {
            notify('Erro ao criar categoria - Não foi possível criar a categoria. Tente novamente.', 'error');
        }
    };

    // Função para simular criação de cliente na API
    const createClientInApi = async (clientName) => {
        try {
            // Simular loading de criação
            notify('Criando cliente - Salvando novo cliente na base de dados.', 'info');

            // Simular delay da API para criar
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Criar valor único baseado no nome
            const newValue = clientName.toLowerCase()
                .replace(/[^a-z0-9\s]/g, '') // Remove caracteres especiais
                .replace(/\s+/g, '-'); // Substitui espaços por hífen

            // Simular recarregamento completo dos clientes após criação
            await reloadClientsFromApi();

            // Adicionar o novo cliente criado (simula que voltou da API)
            const newClientId = Date.now(); // Simular ID único
            clientOptionsBase.value.push({
                value: newClientId,
                label: clientName
            });

            // Selecionar o novo cliente automaticamente
            formData.value.client = newClientId;

            // Fechar modal e mostrar sucesso
            closeCreateClientModal();

            notify(`Cliente criado - Cliente "${clientName}" criado e selecionado com sucesso.`, 'success');

        } catch (error) {
            notify('Erro ao criar cliente - Não foi possível criar o cliente. Tente novamente.', 'error');
        }
    };

    const resetForm = () => {
        formData.value = {
            personType: '',
            isCompanyDocument: false,
            client: '',
            type: '',
            name: '',
            category: '',
            hasExpiryDate: false,
            expiryDate: ''
        };
        selectedFile.value = null;
    };

    const handleFileSelect = (file) => {
        if (file) {
            selectedFile.value = file;
            if (!formData.value.name) {
                // Auto-preenche o nome baseado no arquivo
                formData.value.name = file.name.replace(/\.[^/.]+$/, '');
            }
        }
    };

    const handleUpload = async () => {
        if (!isFormValid.value) {
            notify('Campos obrigatórios - Por favor, preencha todos os campos obrigatórios.', 'warning');
            return;
        }


        // Simular upload com loading
        loadingFull(true);

        try {
            // Simular delay de upload (2-3 segundos)
            await new Promise(resolve => setTimeout(resolve, 2500));

            loadingFull(false);

            emit('upload-success', {
                name: formData.value.name,
                resetForm: resetForm
            });

        } catch (error) {
            loadingFull(false);
            notify('Erro no upload - Ocorreu um erro ao enviar o arquivo. Tente novamente.', 'error');
        }
    };


</script>

<style lang="scss" scoped>
/* =======================
    FORMULÁRIO DE UPLOAD
    ======================= */

.uploadForm_field {
    display: flex;
    flex-direction: column;
    gap: 0.5em;
}

.uploadForm_fieldWithButton {
    display: flex;
    align-items: flex-end;
    gap: 0.5em;

    .button {
        margin-bottom: 0.125em;
        width: 40px !important;
        height: 40px !important;
        padding: 20px !important;
    }
}


.uploadForm_fieldGroup {
    display: flex;
    flex-direction: column;
    gap: 1em;
    padding: 1.25em;
    background: rgba(248, 250, 252, 0.6);
    border: 1px solid rgba(229, 231, 235, 0.8);
    border-radius: 0.75em;
}

.uploadForm_fieldHeader {
    display: flex;
    align-items: center;
    gap: 0.75em;
}


.uploadForm_subFields {
    display: flex;
    flex-direction: column;
    gap: 1em;
    padding-left: 2em;
    border-left: 3px solid var(--colorBlue, #3b82f6);
    margin-left: 0.75em;
}

.uploadForm_inputGroup {
    display: flex;
    align-items: center;
    gap: 0.5em;
}

.uploadForm_inputSuffix {
    font-size: 0.875em;
    color: var(--textSecondary, #6b7280);
    font-weight: 500;
}


.uploadForm_stepActions {
    display: flex;
    gap: 0.75em;
    justify-content: flex-end;
    padding-top: 1em;
    margin-top: 1em;
    border-top: 1px solid rgba(229, 231, 235, 0.5);
}

.emailManager {
    &_actions {
        display: flex;
        justify-content: flex-start;
        margin-bottom: 1em;
    }
    
    &_list {
        display: flex;
        flex-direction: column;
        gap: 0.5em;
    }
    
    &_item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.75em 1em;
        background: white;
        border: 1px solid var(--borderColorLight);
        border-radius: 0.75em;
        font-size: 0.875em;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        transition: all 0.2s ease;
        
        &:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
            border-color: var(--linkColor);
        }
    }
    
    &_info {
        display: flex;
        flex-direction: column;
        gap: 0.25em;
        flex: 1;
    }
    
    &_email {
        color: var(--textPrimary);
        font-weight: 600;
        line-height: 1.2;
    }
    
    &_date {
        color: var(--textSecondary);
        font-size: 0.8em;
        font-weight: 400;
        line-height: 1.3;
        margin-top: 0.1em;
    }
}



.advanced-fields {
    animation: slideIn 0.4s ease-out;
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.step-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 1.5em;
    border-top: 1px solid var(--borderColorLight, #e5e7eb);
    margin-top: 1.5em;
}

.step-actions_spacer {
    flex: 1;
}

.review-summary {
    display: flex;
    flex-direction: column;
    gap: 2em;
}

.review-section {
    padding: 1.5em;
    background: var(--backgroundCard, #f8fafc);
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.75em;
    
    &_title {
        margin: 0 0 1em 0;
        font-size: 1.125em;
        font-weight: 600;
        color: var(--textPrimary, #1e293b);
        border-bottom: 1px solid var(--borderColorLight, #e5e7eb);
        padding-bottom: 0.5em;
    }
}

.review-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 0.75em;
    
    &:last-child {
        margin-bottom: 0;
    }
    
    &_label {
        font-weight: 500;
        color: var(--textSecondary, #64748b);
        min-width: 140px;
        margin-right: 1em;
        font-size: 0.875em;
    }
    
    &_value {
        color: var(--textPrimary, #1e293b);
        font-weight: 600;
        flex: 1;
        font-size: 0.875em;
    }
}

.review-emails {
    display: flex;
    flex-direction: column;
    gap: 0.5em;
    width: 100%;
}

.review-email-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75em 1em;
    background: white;
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.5em;
    font-size: 0.875em;
}

.review-email {
    font-weight: 600;
    color: var(--textPrimary, #1e293b);
}

.review-date {
    font-size: 0.8em;
    color: var(--textSecondary, #64748b);
    font-style: italic;
}


/* ===========================
   MODAL CRIAR CATEGORIA
   =========================== */
.createCategoryModal_header {
    display: flex;
    align-items: center;
    gap: 1em;
    padding: 0.5em 0;
}

.createCategoryModal_icon {
    .sw-icon {
        width: 2.5em;
        height: 2.5em;
        background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
        color: white;
        border-radius: 0.5em;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.25em;
    }
}

.createCategoryModal_title {
    h3 {
        margin: 0 0 0.25em 0;
        font-size: 1.25em;
        font-weight: 600;
        color: var(--textPrimary, #1e293b);
    }

    span {
        font-size: 0.875em;
        color: var(--textSecondary, #64748b);
        line-height: 1.4;
    }
}

.createCategoryModal_content {
    padding: 1em 0;
}

.createCategoryForm {
    display: flex;
    flex-direction: column;
    gap: 1.5em;

    &_actions {
        display: flex;
        gap: 1em;
        justify-content: flex-end;
        padding-top: 1em;
        border-top: 1px solid var(--borderColorLight, #e5e7eb);
        margin-top: 0.5em;
    }
}

/* ===========================
   MODAL CRIAR CLIENTE
   =========================== */
.createClientModal_header {
    display: flex;
    align-items: center;
    gap: 1em;
    padding: 0.5em 0;
}

.createClientModal_icon {
    .sw-icon {
        width: 2.5em;
        height: 2.5em;
        background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
        color: white;
        border-radius: 0.5em;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.25em;
    }
}

.createClientModal_title {
    h3 {
        margin: 0 0 0.25em 0;
        font-size: 1.25em;
        font-weight: 600;
        color: var(--textPrimary, #1e293b);
    }

    span {
        font-size: 0.875em;
        color: var(--textSecondary, #64748b);
        line-height: 1.4;
    }
}

.createClientModal_content {
    padding: 1em 0;
}

.createClientForm {
    display: flex;
    flex-direction: column;
    gap: 1.5em;

    &_actions {
        display: flex;
        gap: 1em;
        justify-content: flex-end;
        padding-top: 1em;
        border-top: 1px solid var(--borderColorLight, #e5e7eb);
        margin-top: 0.5em;
    }
}

/* Responsividade usando mixins do projeto */
@import '@/assets/css/mediaScreen.scss';

@include screen_medias(s, m) {
    .uploadForm {
        gap: 1.25em;
    }

    .uploadForm_fieldGroup {
        padding: 1em;
    }

    .uploadForm_subFields {
        padding-left: 1.5em;
    }

    .step-actions {
        flex-direction: column;
        gap: 1em;

        .step-actions_spacer {
            display: none;
        }
    }
}
</style>